import { CheckCircle2, Hammer, SearchCheck, Shield } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { LeadForm } from "@/components/forms/LeadForm";
import { PageHero } from "@/components/shared/PageHero";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { roofingCategories, roofingTrustPoints } from "@/data/siteEnhancements";
import { useSiteContent } from "@/hooks/useSiteContent";

export default function RoofingPage() {
  const { siteData } = useSiteContent();

  return (
    <div className="space-y-24 pb-6" data-testid="roofing-page">
      <PageHero description="Roof installation, repairs, and inspections that protect the structure beneath your solar or future energy investment." eyebrow="Roofing services" imageUrl={siteData.roofing_page.hero_image} phone={siteData.phone} primaryHref="#roofing-quote-form" primaryLabel="Get roofing quote" testIdPrefix="roofing-hero" title="Reliable roofing work that strengthens performance and peace of mind." />

      <section className="px-4 md:px-6" data-testid="roofing-services-detail-section">
        <div className="page-section space-y-10">
          <SectionHeading description="This service page keeps the structure familiar to classic business websites while making each offer easy to scan and trust." eyebrow="Roofing expertise" testIdPrefix="roofing-sections-heading" title="From repairs to full replacements, Monarch helps owners stay ahead of damage." />
          <div className="grid gap-6 lg:grid-cols-3">
            {siteData.roofing_page.sections.map((section, index) => (
              <Card className="rounded-[2rem] border-white/60 bg-white/90" data-testid={`roofing-service-card-${index + 1}`} key={section.title}>
                <CardContent className="space-y-4 p-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                    {index === 0 ? <Hammer className="h-5 w-5" /> : index === 1 ? <Shield className="h-5 w-5" /> : <SearchCheck className="h-5 w-5" />}
                  </div>
                  <h3 className="text-2xl font-bold text-foreground" data-testid={`roofing-service-title-${index + 1}`}>
                    {section.title}
                  </h3>
                  <p className="text-sm text-muted-foreground" data-testid={`roofing-service-description-${index + 1}`}>
                    {section.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid="roofing-detailed-categories-section">
        <div className="page-section space-y-10">
          <SectionHeading description="These expanded categories explain exactly how Monarch approaches replacements, repairs, inspections, materials, and storm-related roofing support." eyebrow="Expert roofing solutions" testIdPrefix="roofing-detailed-heading" title="Expert roofing solutions built to protect what matters most." />
          <div className="space-y-8">
            {roofingCategories.map((category, index) => (
              <Card className="overflow-hidden rounded-[2rem] border-white/60 bg-white/92 shadow-[0_20px_80px_rgba(16,24,40,0.08)]" data-testid={`roofing-detailed-card-${category.id}`} key={category.id}>
                <CardContent className="grid gap-6 p-4 lg:grid-cols-[0.95fr_1.05fr] lg:items-center lg:p-6">
                  <div className={`${index % 2 === 1 ? "lg:order-2" : ""} image-shell aspect-[4/3] overflow-hidden rounded-[1.6rem]`} data-testid={`roofing-detailed-image-shell-${category.id}`}>
                    <img alt={category.title} className="h-full w-full object-cover" data-testid={`roofing-detailed-image-${category.id}`} src={category.imageUrl} />
                  </div>
                  <div className={`${index % 2 === 1 ? "lg:order-1" : ""} space-y-5`}>
                    <p className="text-sm font-semibold uppercase tracking-[0.28em] text-primary" data-testid={`roofing-detailed-eyebrow-${category.id}`}>
                      {category.eyebrow}
                    </p>
                    <h2 className="text-3xl font-bold text-foreground md:text-4xl" data-testid={`roofing-detailed-title-${category.id}`}>
                      {category.title}
                    </h2>
                    <p className="text-sm text-muted-foreground md:text-base" data-testid={`roofing-detailed-description-${category.id}`}>
                      {category.description}
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {category.highlights.map((highlight) => (
                        <div className="flex items-start gap-3 rounded-[1.2rem] border border-primary/10 bg-primary/5 px-4 py-4" data-testid={`roofing-detailed-highlight-${category.id}-${highlight.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={highlight}>
                          <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary" />
                          <p className="text-sm text-foreground">{highlight}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid="roofing-los-angeles-climate-section">
        <div className="page-section grid gap-8 rounded-[2.5rem] bg-slate-950 px-6 py-10 text-white lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:px-10">
          <div className="space-y-6">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-emerald-300" data-testid="roofing-los-angeles-eyebrow">
              Built for Los Angeles conditions
            </p>
            <h2 className="text-4xl font-bold" data-testid="roofing-los-angeles-title">
              Roofing systems planned for sun exposure, seasonal rain, and long-term property value.
            </h2>
            <p className="text-sm text-white/75 md:text-base" data-testid="roofing-los-angeles-description">
              Los Angeles property owners often need roofing recommendations that balance curb appeal, heat performance, storm readiness, and insurance peace of mind. Monarch helps simplify those choices with practical guidance and durable installation standards.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              {roofingTrustPoints.map((point) => (
                <div className="flex items-start gap-3 rounded-[1.3rem] border border-white/10 bg-white/5 px-4 py-4" data-testid={`roofing-trust-point-${point.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={point}>
                  <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-300" />
                  <p className="text-sm text-white/80">{point}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="image-shell aspect-[5/4] overflow-hidden rounded-[2rem]" data-testid="roofing-los-angeles-image-shell">
            <img alt="Los Angeles homes and roofs" className="h-full w-full object-cover" data-testid="roofing-los-angeles-image" src="https://images.pexels.com/photos/14946525/pexels-photo-14946525.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" />
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid="roofing-quote-section" id="roofing-quote-form">
        <div className="page-section">
          <LeadForm buttonLabel="Get roofing quote" description="Share your roof concerns, repair needs, or replacement goals and we’ll follow up with the best next step." leadType="quote" pageSource="roofing-quote" testIdPrefix="roofing-quote" title="Schedule your roofing consultation" />
        </div>
      </section>
    </div>
  );
}